import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";
import { fetchProducts } from "../lib/api";
import { useCollections, matchesCollection } from "../lib/useCollections";
import { ItemCard } from "./Shop";
import { PageLoader } from "../components/LoadingRing";
import { usePageSeo } from "../lib/usePageSeo";
import { crumbsJsonLd } from "../lib/seoHead";

export default function ShopCollection() {
    const { collectionSlug } = useParams();
    const { data: all = [], isLoading } = useQuery({
        queryKey: ["products"], queryFn: fetchProducts, staleTime: 15000, refetchOnMount: "always",
    });
    const { collections, sections, isLoading: loadingCollections } = useCollections();
    const collection = collections.find((c) => c.slug === collectionSlug);

    usePageSeo(collection ? {
        title: `${collection.name} — ReHealthMe`,
        description: collection.tagline || `${collection.name} from ReHealthMe — formulated in-house, claim-safe and honest about what each product does.`,
        canonical: `${window.location.origin}/shop/${collection.slug}`,
        image: collection.image_url || "",
        jsonLd: [crumbsJsonLd([
            { name: "Home", path: "/" }, { name: "Shop", path: "/shop" },
            { name: collection.name, path: `/shop/${collection.slug}` },
        ])],
    } : null);

    if (loadingCollections) {
        return <PageLoader />;
    }
    if (!collection) {
        return (
            <div className="pt-40 pb-40 text-center" data-testid="collection-not-found">
                <h1 className="font-display text-3xl uppercase tracking-tight">Collection not found</h1>
                <Link to="/shop" className="mono text-[10px] tracking-[0.18em] text-gold mt-5 inline-block">
                    BACK TO SHOP
                </Link>
            </div>
        );
    }

    const items = all.filter((p) => matchesCollection(p, collection, sections));

    return (
        <div className="pt-28 md:pt-32 pb-24" data-testid={`collection-page-${collection.slug}`}>
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <Link to="/shop" data-testid="collection-back"
                    className="inline-flex items-center gap-1.5 mono text-[10px] tracking-[0.18em] text-zinc-500 hover:text-gold">
                    <ChevronLeft className="w-3.5 h-3.5" /> ALL COLLECTIONS
                </Link>

                <div className="mt-5 max-w-2xl">
                    <div className="eyebrow mb-3">Shop</div>
                    <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tight">{collection.name}</h1>
                    {collection.tagline && <p className="mt-4 text-zinc-500">{collection.tagline}</p>}
                    {collection.description && (
                        <p className="mt-3 text-sm text-zinc-500 leading-relaxed">{collection.description}</p>
                    )}
                </div>

                <div className="mt-3 mono text-[10px] tracking-[0.18em] text-zinc-500" data-testid="collection-item-count">
                    {isLoading ? "LOADING…" : `${items.length} ${items.length === 1 ? "ITEM" : "ITEMS"}`}
                </div>

                {!isLoading && !items.length ? (
                    <div className="mt-10 border border-white/10 bg-bg1 p-12 text-center" data-testid="collection-empty">
                        <div className="font-display text-2xl uppercase tracking-tight">Nothing here yet</div>
                        <p className="text-sm text-zinc-500 mt-2">
                            This collection is live but empty — new items land here as soon as they are published.
                        </p>
                        <Link to="/shop" className="mono text-[10px] tracking-[0.18em] text-gold mt-5 inline-block">
                            BROWSE OTHER COLLECTIONS
                        </Link>
                    </div>
                ) : (
                    <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
                        data-testid="collection-grid-items">
                        {items.map((p) => <ItemCard key={p.id} p={p} />)}
                    </div>
                )}

                <div className="mt-16 flex flex-wrap gap-3" data-testid="collection-switcher">
                    {collections.filter((c) => c.slug !== collection.slug).map((c) => (
                        <Link key={c.id} to={`/shop/${c.slug}`} data-testid={`collection-chip-${c.slug}`}
                            className="mono text-[10px] tracking-[0.16em] border border-white/12 px-4 py-2.5 text-zinc-400 hover:border-gold hover:text-gold transition-colors">
                            {c.name.toUpperCase()}
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}
